/**
 * <s-back-to-top>: floating "back to top" button.
 * Shows only when the visitor nears the bottom of the page (footer area), hides elsewhere,
 * and scrolls smoothly back up on click (instant when reduced motion is preferred).
 */
class SBackToTop extends HTMLElement {
  #ac;
  #frame;

  connectedCallback() {
    this.#ac?.abort();
    this.#ac = new AbortController();
    const options = { signal: this.#ac.signal, passive: true };

    this.addEventListener('command', (event) => {
      if (event.command === '--scroll-top') this.#scrollTop();
    }, { signal: this.#ac.signal });

    window.addEventListener('scroll', () => this.#queueSync(), options);
    window.addEventListener('resize', () => this.#queueSync(), options);
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #queueSync() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#sync();
    });
  }

  #sync() {
    // Only near the end of the page: within ~3/4 of a screen (min 500px) of the bottom, and never at the very top of short pages.
    const doc = document.documentElement;
    const distanceToBottom = doc.scrollHeight - (window.scrollY + window.innerHeight);
    const nearBottom = distanceToBottom <= Math.max(500, window.innerHeight * 0.75);
    this.toggleAttribute('data-visible', window.scrollY > 300 && nearBottom);
  }

  #scrollTop() {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
    const main = document.getElementById('main-content');
    main?.focus({ preventScroll: true });
  }
}

if (!customElements.get('s-back-to-top')) customElements.define('s-back-to-top', SBackToTop);
